import {
  mapUploadIntentDto,
  mapUploadedImageDto,
  mapUploadedVideoDto,
  type UploadIntentResultDto,
  type UploadedImageDto,
  type UploadedVideoDto,
} from '@/types';

import type { GcsPutFn } from '../assetLibrary';
import type { UploadedAudio, CreateImageIntentRequest,
  CreateVideoIntentRequest,
  UploadsService, } from '../uploadsService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`UploadsService`
 * -----------------------------------------------------------------------------
 * 對齊 Phase 2 doc：每個方法都解掉 :type:`BaseResponse` 殼後做 snake_case →
 * camelCase mapping，**完全套用既有 ``httpRequestWithRefresh``**（cookie + CSRF +
 * 401 refresh chain 全自動），不重造 wrapper。
 *
 * @see docs/UPLOADS.md §2
 */

/** 內部把 query path 統一加 ``/api`` 前綴，省得每處都拼。 */
const PATH = {
  imagesIntent: '/api/uploads/images/intent',
  imagesFinalize: '/api/uploads/images/finalize',
  imagesItem: (id: string) => `/api/uploads/images/${encodeURIComponent(id)}`,
  videosIntent: '/api/uploads/videos/intent',
  audioIntent: '/api/uploads/audio/intent',
  audioFinalize: '/api/uploads/audio/finalize',
  audioItem: (id: string) => `/api/uploads/audio/${encodeURIComponent(id)}`,
  videosFinalize: '/api/uploads/videos/finalize',
  videosItem: (id: string) => `/api/uploads/videos/${encodeURIComponent(id)}`,
} as const;

export function createHttpUploadsService(config: HttpClientConfig): UploadsService {
  return {
    images: {
      async createIntent(req: CreateImageIntentRequest) {
        const env = await httpRequestWithRefresh<UploadIntentResultDto>(
          config,
          'POST',
          PATH.imagesIntent,
          { mime_type: req.mimeType, size_bytes: req.sizeBytes },
        );
        return mapUploadIntentDto(unwrapResult(env, 'uploads.images.createIntent'));
      },

      async finalize(uploadId: string) {
        const env = await httpRequestWithRefresh<UploadedImageDto>(
          config,
          'POST',
          PATH.imagesFinalize,
          { upload_id: uploadId },
        );
        return mapUploadedImageDto(unwrapResult(env, 'uploads.images.finalize'));
      },


      async get(uploadId: string) {
        const env = await httpRequestWithRefresh<UploadedImageDto>(
          config,
          'GET',
          PATH.imagesItem(uploadId),
        );
        return mapUploadedImageDto(unwrapResult(env, 'uploads.images.get'));
      },

    },

    // T13(docs/50)— BE `/uploads/videos/*` 早已就緒,解 stub 接真(mirror image)。
    videos: {
      async createIntent(req: CreateVideoIntentRequest) {
        const env = await httpRequestWithRefresh<UploadIntentResultDto>(
          config,
          'POST',
          PATH.videosIntent,
          { mime_type: req.mimeType, size_bytes: req.sizeBytes, duration_seconds: req.durationSeconds },
        );
        return mapUploadIntentDto(unwrapResult(env, 'uploads.videos.createIntent'));
      },
      async finalize(uploadId: string) {
        const env = await httpRequestWithRefresh<UploadedVideoDto>(
          config,
          'POST',
          PATH.videosFinalize,
          { upload_id: uploadId },
        );
        return mapUploadedVideoDto(unwrapResult(env, 'uploads.videos.finalize'));
      },
      async get(uploadId: string) {
        const env = await httpRequestWithRefresh<UploadedVideoDto>(
          config,
          'GET',
          PATH.videosItem(uploadId),
        );
        return mapUploadedVideoDto(unwrapResult(env, 'uploads.videos.get'));
      },
    },

    // R363(BE cut.3,spec §13.3b)— 剪輯配樂上傳:同 image / video 的兩階段,
    // 差異見 interface 檔;intent 刻意不送 duration_seconds(多帶 = 422)。
    audio: {
      async createIntent(req) {
        const env = await httpRequestWithRefresh<UploadIntentResultDto>(
          config,
          'POST',
          PATH.audioIntent,
          { mime_type: req.mimeType, size_bytes: req.sizeBytes },
        );
        return mapUploadIntentDto(unwrapResult(env, 'uploads.audio.createIntent'));
      },
      async finalize(uploadId: string) {
        const env = await httpRequestWithRefresh<UploadedAudioDto>(
          config,
          'POST',
          PATH.audioFinalize,
          { upload_id: uploadId },
        );
        return mapUploadedAudioDto(unwrapResult(env, 'uploads.audio.finalize'));
      },
      async get(uploadId: string) {
        const env = await httpRequestWithRefresh<UploadedAudioDto>(
          config,
          'GET',
          PATH.audioItem(uploadId),
        );
        return mapUploadedAudioDto(unwrapResult(env, 'uploads.audio.get'));
      },
    },

  };
}

/**
 * HTTP impl of :type:`GcsPutFn`
 * -----------------------------------------------------------------------------
 * **不**走 ``httpRequestWithRefresh``：
 *   - GCS 是 cross-origin，signed URL 自帶授權（query string 裡的 ``X-Goog-Signature``）
 *   - 多帶 cookie 會被 CORS 擋下；多帶 ``X-CSRF-Token`` GCS 不認得，徒增 preflight
 *   - 401 也不該觸發後端 refresh chain — 是 GCS 自己 reject，不是 session 失效
 *
 * 必帶 headers：
 *   - ``Content-Type``：必須跟 intent 宣告完全一致；GCS 強制驗
 *   - ``x-goog-content-length-range``：``sizeRangeHeader`` 直接 forward —— caller
 *     （``assetLibrary``）已經從 ``intent.maxSizeBytes`` 計算過，**必須**跟後端
 *     sign 時包進 ``X-Goog-SignedHeaders`` 的 value 完全一致，否則 GCS 回 403
 *     SignatureDoesNotMatch。**不要**在這層用 ``file.size`` 重新組（R18 修正）。
 */
interface UploadedAudioDto {
  upload_id?: unknown;
  mime_type?: unknown;
  size_bytes?: unknown;
  download_url?: unknown;
  download_url_expires_datetime?: unknown;
}

function mapUploadedAudioDto(dto: UploadedAudioDto): UploadedAudio {
  return {
    uploadId: typeof dto.upload_id === 'string' ? dto.upload_id : '',
    mimeType: typeof dto.mime_type === 'string' ? dto.mime_type : '',
    sizeBytes: typeof dto.size_bytes === 'number' ? dto.size_bytes : 0,
    downloadUrl: typeof dto.download_url === 'string' ? dto.download_url : '',
    downloadUrlExpiresDatetime:
      typeof dto.download_url_expires_datetime === 'string' ? dto.download_url_expires_datetime : '',
  };
}

export function createHttpGcsPut(opts?: { fetchImpl?: typeof fetch }): GcsPutFn {
  const fetchImpl = opts?.fetchImpl ?? globalThis.fetch.bind(globalThis);
  return async (uploadUrl: string, file: File, sizeRangeHeader: string) => {
    const res = await fetchImpl(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
        'x-goog-content-length-range': sizeRangeHeader,
      },
      // 重要：**不**帶 credentials；GCS 是 cross-origin 用 signed URL 授權
    });
    if (!res.ok) {
      throw new Error(`gcs_put_failed:${res.status}`);
    }
  };
}
